import React, { useEffect, useRef, useMemo } from 'react';
import { ErrorPage } from '@components/ErrorPage';
import { Redirect } from '@components/Redirect';
import { PageHelmet } from '@components/PageHelmet';
import { ensureAnchorIsLoaded } from '@helpers/window';
import { useAnimationObserver } from '@hooks/useAnimationObserver';
import { checkIfReduceMotion } from '@helpers/browserHelper';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { Page } from '@models/page';
import { MobileDemoBanner } from '@components/MobileDemoBanner/MobileDemoBanner';
import { PageVariantProvider } from '@contexts/PageVariantContext';
import { ConditionalPageSections } from '@components/ConditionalPageSections';

const classes = {
    mainContent: {
        transition: 'padding 225ms cubic-bezier(0, 0, 0.2, 1)',
    },
};

function formSeo(page: Page, prefixStringWithBaseUrl: (value?: string) => string): Record<string, unknown> {
    const { title, description, canonical, modifiedDateUtc, prefixedImagePath } = page;
    return prefixedImagePath
        ? {
            title,
            description,
            canonical,
            dateModified: modifiedDateUtc,
            imageUrl: prefixStringWithBaseUrl(prefixedImagePath),
        }
        : { title, description, canonical, dateModified: modifiedDateUtc };
}

export default function PageSlug(): React.ReactElement {
    const { page, prefixStringWithBaseUrl } = useStaticValues();
    const reducedMotion = useRef(false);
    const pageChange = useMemo(() => !!page && !reducedMotion.current, [page, reducedMotion]);
    useAnimationObserver(pageChange);
    const disableMobileDemoBanner = page?.mobileDemoBannerDisabled;

    useEffect(() => {
        if (window) {
            reducedMotion.current = checkIfReduceMotion(window);
        }
    }, []);

    useEffect(() => {
        ensureAnchorIsLoaded();
    }, [page]);

    return page ? (
        page.redirectUrl ? (
            <Redirect redirectUrl={page.redirectUrl} statusCode={200} />
        ) : (
            <div css={classes.mainContent}>
                <div>
                    <PageHelmet seo={formSeo(page, prefixStringWithBaseUrl)} noIndex={page.noIndex} />
                    {!disableMobileDemoBanner ? <MobileDemoBanner /> : null}
                    {Array.isArray(page.sections) ? (
                        <PageVariantProvider pageVariants={page.variants}>
                            <ConditionalPageSections sections={page.sections} />
                        </PageVariantProvider>
                    ) : null}
                </div>
            </div>
        )
    ) : (
        <ErrorPage />
    );
}
